import { Component, Inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CustomMessageService } from '@app/shared/components/custom-message/custom-message.service';
import { LoggerService } from '@app/logger/logger.service';
import { StockEquipmentsService } from '@app/shared/services/entities/stockEquipments.service';

import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MaterialModule } from '@app/material.module';
const log = new LoggerService('EQUIPMENTS FORM COMPONENT');

// Enum para tipos de mantenimiento
export enum MaintenanceType {
  PREVENTIVO = 'preventivo',
  CORRECTIVO = 'correctivo'
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MaterialModule,
  ],
  selector: 'app-maintenanceSchedule-form',
  templateUrl: './maintenanceSchedule-form.component.html',
  styleUrls: ['./maintenanceSchedule-form.component.scss']
})
export class MaintenanceScheduleFormComponent implements OnInit {

  public formGroup: FormGroup;
  public activeContractList = null;
  public contractList = null;
  public contractObj = null;
  public record = null;
  public preventiveMaintenanceSchedule: any[] = [];
  public serviceDates: string[] = [];
  public minDate = new Date(); // Fecha mínima para el DatePicker

  // Opciones para el tipo de mantenimiento
  public maintenanceTypes = [
    { value: MaintenanceType.PREVENTIVO, label: 'Preventivo', icon: 'schedule' },
    { value: MaintenanceType.CORRECTIVO, label: 'Correctivo', icon: 'build' }
  ];

  constructor(
    public dialogRef: MatDialogRef<MaintenanceScheduleFormComponent>,
    private customMessageService: CustomMessageService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private formBuilder: FormBuilder,
    private inventoryEquipmentsService: StockEquipmentsService,
    private cdr: ChangeDetectorRef
  ) { }

  async ngOnInit() {
    
    this.activeContractList = this.data.contract.filter(element => element.status === true) || [];
    this.contractList = this.data.contract;
    this.contractObj = this.data.contractObj;
    this.record = this.data.record;
    this.preventiveMaintenanceSchedule = this.data.record.preventiveMaintenanceSchedule || [];

    // Determinar el contrato inicial basado en datos existentes
    let initialContract = null;
    
    if (this.preventiveMaintenanceSchedule.length > 0) {
      // Si hay programación existente, buscar el contrato correspondiente
      const existingContractId = this.preventiveMaintenanceSchedule[0].contract;
      initialContract = this.activeContractList.find(contract => contract.id === existingContractId);
      log.debug('Contrato encontrado con datos existentes:', initialContract);
    }
    
    // Si no se encuentra o no hay datos, usar el primer contrato activo
    if (!initialContract) {
      initialContract = this.activeContractList[0] || null;
      log.debug('Usando primer contrato activo:', initialContract);
    }

    this.formGroup = this.formBuilder.group({
      selectedContract: [initialContract, Validators.required],
      newServiceDate: [null],
      maintenanceType: [MaintenanceType.PREVENTIVO, Validators.required] // Valor por defecto: preventivo
    });

    // Cargar las fechas de servicio existentes DESPUÉS de crear el form
    this.loadServiceDatesForContract();

    // Suscribirse a cambios en el contrato seleccionado
    this.formGroup.get('selectedContract')?.valueChanges.subscribe(contract => {
      if (contract) {
        this.loadServiceDatesForContract();
      }
    });

    log.debug('Data inicial:', this.data);
    log.debug('Programación preventiva inicial:', this.preventiveMaintenanceSchedule);
  }

  /**@description Obtener fechas de servicio con información del contrato */
  getServiceDatesWithContracts() {
    return this.preventiveMaintenanceSchedule
      .map(schedule => ({
        date: schedule.date,
        contract: schedule.contract,
        type: schedule.type || MaintenanceType.PREVENTIVO // Backward compatibility
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }

  /**@description Cargar fechas de servicio para el contrato activo */
  private loadServiceDatesForContract() {
    const selectedContract = this.formGroup?.get('selectedContract')?.value;
    
    if (!selectedContract || !selectedContract.id) {
      this.serviceDates = [];
      log.debug('No hay contrato seleccionado, limpiando fechas');
      return;
    }

    const contractSchedules = this.preventiveMaintenanceSchedule.filter(
      schedule => schedule.contract === selectedContract.id
    );

    this.serviceDates = contractSchedules
      .map(schedule => schedule.date)
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

    log.debug('Fechas cargadas para contrato:', selectedContract.id);
    log.debug('Fechas encontradas:', this.serviceDates);
  }

  /**@description Obtener icono según el tipo de mantenimiento */
  getMaintenanceTypeIcon(type: string): string {
    return type === MaintenanceType.CORRECTIVO ? 'build' : 'schedule';
  }

  /**@description Obtener color según el tipo de mantenimiento */
  getMaintenanceTypeColor(type: string): string {
    return type === MaintenanceType.CORRECTIVO ? 'warn' : 'primary';
  }

  /**@description Obtener etiqueta según el tipo de mantenimiento */
  getMaintenanceTypeLabel(type: string): string {
    return type === MaintenanceType.CORRECTIVO ? 'Correctivo' : 'Preventivo';
  }

  /**@description Agregar nueva fecha de servicio */
  addServiceDate() {
    const selectedContract = this.formGroup?.get('selectedContract')?.value;
    
    if (!selectedContract) {
      this.customMessageService.open({
        title: 'Cuidado', 
        type: 'warning', 
        message: 'Seleccione un contrato primero.', 
        dismissBtn: true
      });
      return;
    }

    const newDate = this.formGroup.get('newServiceDate')?.value;
    const maintenanceType = this.formGroup.get('maintenanceType')?.value;
    
    if (!newDate) {
      this.customMessageService.open({
        title: 'Cuidado', 
        type: 'warning', 
        message: 'Seleccione una fecha de servicio.', 
        dismissBtn: true
      });
      return;
    }

    if (!maintenanceType) {
      this.customMessageService.open({
        title: 'Cuidado', 
        type: 'warning', 
        message: 'Seleccione un tipo de mantenimiento.', 
        dismissBtn: true
      });
      return;
    }

    // Convertir la fecha a string en formato ISO
    const dateString = new Date(newDate).toISOString().split('T')[0];

    // Para mantenimiento correctivo, permitir fechas pasadas
    if (maintenanceType === MaintenanceType.PREVENTIVO) {
      const today = new Date().toISOString().split('T')[0];
      if (dateString < today) {
        this.customMessageService.open({
          title: 'Cuidado', 
          type: 'warning', 
          message: 'No se pueden programar fechas preventivas anteriores a hoy.', 
          dismissBtn: true
        });
        return;
      }
    }

    // Verificar si la fecha ya existe para este contrato
    const existingSchedule = this.preventiveMaintenanceSchedule.find(
      schedule => schedule.contract === selectedContract.id && 
                 schedule.date === dateString &&
                 schedule.type === maintenanceType
    );

    if (existingSchedule) {
      this.customMessageService.open({
        title: 'Cuidado', 
        type: 'warning', 
        message: `Esta fecha de servicio ${this.getMaintenanceTypeLabel(maintenanceType).toLowerCase()} ya existe para este contrato.`, 
        dismissBtn: true
      });
      return;
    }

    // Agregar a preventiveMaintenanceSchedule
    this.preventiveMaintenanceSchedule.push({
      contract: selectedContract.id,
      date: dateString,
      type: maintenanceType
    });

    // Recargar las fechas del contrato seleccionado
    this.loadServiceDatesForContract();

    // Limpiar el campo de fecha pero mantener el tipo de mantenimiento
    this.formGroup.get('newServiceDate')?.setValue(null);

    log.debug('Fecha agregada:', dateString, 'al contrato:', selectedContract.id, 'tipo:', maintenanceType);
    log.debug('Programación preventiva actualizada:', this.preventiveMaintenanceSchedule);
  }

  /**@description Comparar contratos para mat-select */
  compareContracts(c1: any, c2: any): boolean {
    return c1 && c2 ? c1.id === c2.id : c1 === c2;
  }

  /**@description Eliminar fecha de servicio */
  removeServiceDateMaterial(dateToRemove: string, contractToRemove: string, event?: Event) {
    // Prevenir propagación del evento
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    try {
      log.debug('=== INICIANDO ELIMINACIÓN ===');
      log.debug('Fecha a eliminar:', dateToRemove);
      log.debug('Contrato a eliminar:', contractToRemove);

      if (!contractToRemove) {
        this.customMessageService.open({
          title: 'Error', 
          type: 'warning', 
          message: 'No se puede eliminar: no hay contrato especificado.', 
          dismissBtn: true
        });
        return;
      }

      // Encontrar el índice del elemento a eliminar
      const indexToRemove = this.preventiveMaintenanceSchedule.findIndex(
        schedule => schedule.contract === contractToRemove && schedule.date === dateToRemove
      );

      if (indexToRemove === -1) {
        this.customMessageService.open({
          title: 'Advertencia', 
          type: 'warning', 
          message: 'No se encontró la fecha de servicio para eliminar.', 
          dismissBtn: true
        });
        return;
      }

      // Eliminar el elemento
      this.preventiveMaintenanceSchedule.splice(indexToRemove, 1);

      // Recargar las fechas del contrato seleccionado si existe
      this.loadServiceDatesForContract();
      
      // Forzar detección de cambios
      this.cdr.detectChanges();

      // Mensaje de confirmación
      this.customMessageService.open({
        title: 'Éxito', 
        type: 'success', 
        message: 'Fecha de servicio eliminada correctamente.', 
        duration: 2000
      });

    } catch (error) {
      log.error('Error al eliminar fecha de servicio:', error);
      this.customMessageService.open({
        title: 'Error', 
        type: 'error', 
        message: 'Error al eliminar la fecha de servicio.', 
        dismissBtn: true
      });
    }
  }

  /**@description TrackBy function para mejorar performance del ngFor */
  trackByDate(index: number, item: any): string {
    return `${item.date}-${item.contract}-${item.type}`;
  }

  /**@description Función para actualizar equipamiento */
  async update(){
    try{

      if(!this.preventiveMaintenanceSchedule || this.preventiveMaintenanceSchedule.length === 0){
        await this.customMessageService.open({title: 'Cuidado', type: 'warning', message: 'Es necesario programar al menos una fecha de servicio.', dismissBtn: true})
        return;
      }
  
      let data = JSON.parse(JSON.stringify(this.record));
      
      // Actualizar con la programación de mantenimiento preventivo
      data['preventiveMaintenanceSchedule'] = this.preventiveMaintenanceSchedule;
      data['id'] = this.record['id']
      data['operationType'] = 'single'
      log.debug('data',data)

      this.customMessageService.open({title: 'Espere', type: 'success', message: 'Guardando programación de servicio', loading: true });
      let apiResponse = await this.inventoryEquipmentsService.update(data);
      this.customMessageService.close();
      if (apiResponse.state == 'success') {
        await this.customMessageService.open({title: '¡Hecho!', type: 'success', message: apiResponse['message'], duration: 2000 });
        
        // Incluir la programación de mantenimiento preventivo en la respuesta
        const responseData = {
          ...apiResponse.data,
          preventiveMaintenanceSchedule: this.preventiveMaintenanceSchedule
        };
        
        this.dialogRef.close(responseData);
      }else{
        await this.customMessageService.open({title: 'Error', type: 'error', message: apiResponse['message'], dismissBtn: true });
      }

    }catch(err){
      this.customMessageService.close();
      await this.customMessageService.open({title: 'Error', type: 'error', message: err, dismissBtn: true });
    }
  }

}